import { authClient } from '@/auth/auth';
import type { ConnectionType, ConnectionTypeDetails } from 'quadratic-shared/typesAndSchemasConnections';
import z from 'zod';
const API_URL = import.meta.env.VITE_QUADRATIC_CONNECTION_URL;

const jwtHeader = async (): Promise<HeadersInit> => {
  let jwt = await authClient.getTokenOrRedirect();
  return { 'content-type': 'application/json', authorization: `Bearer ${jwt}` };
};

// TODO: these should come from the connection service definition for these
// endpoints but for now, they are defined here
const TestSchema = z.object({ connected: z.boolean(), message: z.string().nullable() });

const SqlSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  database: z.string(),
  tables: z.array(
    z.object({
      name: z.string(),
      schema: z.string(), // public or ...?
      columns: z.array(
        z.object({
          name: z.string(),
          type: z.string(),
          is_nullable: z.boolean(),
        })
      ),
    })
  ),
});
export type SqlSchemaResponse = z.infer<typeof SqlSchema>;

const StaticIpsSchema = z.object({
  static_ips: z.array(z.string()),
});
type StaticIpsResponse = z.infer<typeof StaticIpsSchema>;

// To add a new financial endpoint, add a single entry here.
// FinancialEndpoint, FinancialRequestMap, and runtime validation all derive from this.
const financialSchemas = {
  'stock-prices': z.object({
    identifier: z.string(),
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
    frequency: z.string().nullable().optional(),
  }),
  'financial-statements': z.object({
    identifier: z.string(),
    statement: z.string(),
    fiscal_year: z.number(),
    fiscal_period: z.string(),
  }),
  'realtime-price': z.object({
    identifier: z.string(),
  }),
  dividends: z.object({
    identifier: z.string(),
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
  }),
  splits: z.object({
    identifier: z.string(),
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
  }),
  company: z.object({
    identifier: z.string(),
  }),
  'company-news': z.object({
    identifier: z.string(),
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
    page_size: z.number().nullable().optional(),
  }),
  'data-point': z.object({
    identifier: z.string(),
    tag: z.string(),
  }),
  'data-point-number': z.object({
    identifier: z.string(),
    tag: z.string(),
  }),
  'data-point-text': z.object({
    identifier: z.string(),
    tag: z.string(),
  }),
  'historical-data': z.object({
    identifier: z.string(),
    tag: z.string(),
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
    frequency: z.string().nullable().optional(),
    sort_order: z.string().nullable().optional(),
  }),
  'intraday-prices': z.object({
    identifier: z.string(),
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
    interval_size: z.string().nullable().optional(),
  }),
  technicals: z.object({
    identifier: z.string(),
    indicator: z.string(),
    period: z.number().nullable().optional(),
    signal_period: z.number().nullable().optional(),
    fast_period: z.number().nullable().optional(),
    slow_period: z.number().nullable().optional(),
    standard_deviations: z.number().nullable().optional(),
    constant: z.number().nullable().optional(),
    price_key: z.string().nullable().optional(),
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
  }),
  'search-companies': z.object({
    query: z.string(),
    page_size: z.number().nullable().optional(),
  }),
  'search-securities': z.object({
    query: z.string(),
    page_size: z.number().nullable().optional(),
  }),
  'economic-data': z.object({
    identifier: z.string(),
    tag: z.string(),
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
    sort_order: z.string().nullable().optional(),
  }),
  'sic-data': z.object({
    identifier: z.string(),
    tag: z.string(),
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
    sort_order: z.string().nullable().optional(),
  }),
  ipos: z.object({
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
    status: z.string().nullable().optional(),
  }),
} satisfies Record<string, z.ZodSchema>;

export type FinancialEndpoint = keyof typeof financialSchemas;
type FinancialRequestMap = { [E in FinancialEndpoint]: z.infer<(typeof financialSchemas)[E]> };
export type FinancialRequestParams = FinancialRequestMap[FinancialEndpoint];

export type QueryPreviewColumn = { name: string; data_type: string };
export type QueryPreviewResponse = {
  columns: QueryPreviewColumn[];
  rows: Array<Record<string, unknown>>;
};

export const connectionClient = {
  query: {
    preview: async (
      connectionType: string,
      connectionId: string,
      teamUuid: string,
      query: string,
      timeout: number = 30000
    ): Promise<QueryPreviewResponse | null> => {
      try {
        const loggedIn = await authClient.isAuthenticated();
        if (!loggedIn) return null;

        const typeLower = connectionType.toLowerCase().replace(/_/g, '-');
        const headers = new Headers(await jwtHeader());
        headers.set('X-Team-Id', teamUuid);

        const res = await fetch(`${API_URL}/${typeLower}/query`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ connection_id: connectionId, query, format: 'json' }),
          signal: AbortSignal.timeout(timeout),
        });

        if (!res.ok) {
          console.error('Query preview failed:', await res.text());
          return null;
        }

        return (await res.json()) as QueryPreviewResponse;
      } catch (err) {
        console.error('Failed to fetch query preview', err);
        return null;
      }
    },
  },
  schemas: {
    // ignore case of connection type
    get: async (
      connectionType:
        | 'postgres'
        | 'POSTGRES'
        | 'mysql'
        | 'MYSQL'
        | 'mssql'
        | 'MSSQL'
        | 'snowflake'
        | 'SNOWFLAKE'
        | 'cockroachdb'
        | 'COCKROACHDB'
        | 'BIGQUERY'
        | 'mariadb'
        | 'MARIADB'
        | 'supabase'
        | 'SUPABASE'
        | 'neon'
        | 'NEON'
        | 'mixpanel'
        | 'MIXPANEL'
        | 'google_analytics'
        | 'GOOGLE_ANALYTICS'
        | 'plaid'
        | 'PLAID'
        | 'quickbooks'
        | 'QUICKBOOKS',
      connectionId: string,
      teamUuid: string,
      forceCacheRefresh: boolean = false,
      timeout: number = 60000
    ): Promise<SqlSchemaResponse | null> => {
      // This might get called on a public file (where the user might not be
      // logged in but can still access the file). If they're not logged in,
      // we won't make the request (to prevent the redirect). It'll fail silently
      const loggedIn = await authClient.isAuthenticated();
      if (!loggedIn) {
        console.log("User is not logged in, so we won't make a request to the connection service.");
        return null;
      }

      const headers = new Headers(await jwtHeader());
      headers.set('X-Team-Id', teamUuid);

      const url = `${API_URL}/${connectionType.toLowerCase().replace(/_/g, '-')}/schema/${connectionId}?force_cache_refresh=${forceCacheRefresh}`;
      const res = await fetch(url, {
        method: 'GET',
        headers,
        signal: AbortSignal.timeout(timeout),
      });
      if (!res.ok) {
        throw new Error('Failed to get the schema from the connection service');
      }
      const data = await res.json();
      return SqlSchema.parse(data);
    },
  },
  test: {
    run: async ({
      type,
      typeDetails,
      teamUuid,
    }: {
      type: ConnectionType;
      typeDetails: ConnectionTypeDetails;
      teamUuid: string;
    }) => {
      try {
        const typeLower = type.toLowerCase().replace(/_/g, '-');
        const headers = new Headers(await jwtHeader());
        headers.set('X-Team-Id', teamUuid);

        const res = await fetch(`${API_URL}/${typeLower}/test`, {
          method: 'POST',
          headers,
          body: JSON.stringify(typeDetails),
        });
        const data = await res.json();
        return TestSchema.parse(data);
      } catch (err) {
        console.error('Failed to connect to connection service', err);
        return {
          connected: false,
          message:
            'Network error: failed to make connection. Make sure you’re connected to the internet and try again.',
        };
      }
    },
  },
  staticIps: {
    list: async (): Promise<string[] | null> => {
      try {
        const res = await fetch(`${API_URL}/static-ips`, {
          method: 'GET',
          headers: new Headers(await jwtHeader()),
        });
        const data = await res.json();
        const { static_ips } = StaticIpsSchema.parse(data) as StaticIpsResponse;
        return static_ips;
      } catch (err) {
        console.error('Failed to get the static ips from the connection service', err);
        return null;
      }
    },
  },
  financial: {
    request: async <E extends FinancialEndpoint>(
      teamUuid: string,
      endpoint: E,
      params: FinancialRequestMap[E]
    ): Promise<{ data: unknown; error: string | null }> => {
      try {
        const schema = financialSchemas[endpoint];
        const validated = schema.parse(params);

        // The /financial endpoint accepts anonymous requests, so don't trigger
        // a login redirect for non-logged-in users — pass through whatever
        // token is already cached, otherwise omit the Authorization header.
        const jwt = await authClient.getTokenOrRedirect(true);
        const headers = new Headers({ 'content-type': 'application/json' });
        if (jwt) {
          headers.set('authorization', `Bearer ${jwt}`);
        }
        if (teamUuid) {
          headers.set('X-Team-Id', teamUuid);
        }

        const res = await fetch(`${API_URL}/financial`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ endpoint, params: validated }),
        });

        if (!res.ok) {
          const errorText = await res.text();
          return {
            data: null,
            error: `Financial data request failed with status ${res.status}: ${errorText}`,
          };
        }

        const data = await res.json();
        return { data, error: null };
      } catch (err) {
        if (err instanceof z.ZodError) {
          return {
            data: null,
            error: `Invalid request parameters: ${err.errors.map((e) => e.message).join(', ')}`,
          };
        }
        console.error('Failed to fetch financial data from connection service', err);
        return {
          data: null,
          error: err instanceof Error ? err.message : 'Unknown error fetching financial data',
        };
      }
    },
  },
};
